import { useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import {
  ApiError,
  useCustomersControllerUpdate,
  useRoutingControllerDistance,
  useRoutingControllerCustomerDistance,
  useCustomersControllerUpdateProperty,
  useCustomersControllerUpdateUnit,
  useCustomersControllerUpdateContact,
  useCustomersControllerCreateContact,
  useBillingControllerSetTravelBilling,
  useArticlesControllerList,
} from '@werkbank/shared';
import { formatFieldErrors } from '../lib/fieldErrors';
import { Icon } from './navIcons';
import { CHANNEL_LABELS, selectOptions } from '../lib/format';

const TRAVEL_MODES: { value: string; label: string }[] = [
  { value: 'NONE', label: 'Keine automatische Fahrt' },
  { value: 'FLAT', label: 'Pauschale je Fahrt (Artikel)' },
  { value: 'PER_KM', label: 'Nach Kilometern (Entfernung × Faktor)' },
  { value: 'TRIP_POSITIONS', label: 'Erste/weitere Fahrt (zwei Artikel)' },
  { value: 'ZONES', label: 'Zonen (aus Einstellungen)' },
];

/**
 * Stammdaten-Pflegedialoge (4.1b): Bearbeiten und Archivieren für Gebäude,
 * Wohnung (inkl. tenantEmail), Ansprechpartner sowie der
 * Kunden-Bearbeiten-Dialog (Stammfelder, Kanal, Zahlungskonditionen, Debitor).
 */

interface DialogProps<T> {
  entity: T | null;
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Vollanalyse M13: gemeinsames Gerüst der vier Pflegedialoge — form-Deltas
 * (nur geänderte Felder, Entitätswert als Fallback), Fehlerzeile und die
 * Standard-Verdrahtung der Speicher-Mutation (onSuccess: onSaved + onClose,
 * onError: Meldung anzeigen).
 */
function useEditDialogForm<V = unknown>({
  onClose,
  onSaved,
  resetOnSuccess = false,
}: {
  onClose: () => void;
  onSaved: () => void;
  /** Deltas nach dem Speichern verwerfen (Dialoge mit Anlege-Modus). */
  resetOnSuccess?: boolean;
}) {
  const [form, setForm] = useState<Record<string, V>>({});
  const [error, setError] = useState<string | null>(null);
  const mutation = {
    onSuccess: () => {
      onSaved();
      onClose();
      if (resetOnSuccess) setForm({});
    },
    onError: (e: { message: string }) => setError(e.message),
  };
  /** String-Feld: Formular-Delta vor Entitätswert. */
  const val = (k: string, fallback: string) => String(form[k] ?? fallback ?? '');
  /** Generischer Feld-Zugriff mit typisiertem Fallback (Kunden-Dialog). */
  const v = <T,>(k: string, fallback: T): T =>
    form[k] === undefined ? fallback : (form[k] as unknown as T);
  return { form, setForm, error, setError, val, v, mutation };
}

/**
 * Vollanalyse M13: gemeinsamer Dialog-Abschluss — Fehler-Alert plus
 * Abbrechen/Speichern-Leiste mit loading und data-testid-Durchreichung.
 */
export function EditDialogFooter({
  error,
  onCancel,
  onSave,
  saving,
  saveTestId,
  saveLabel = 'Speichern',
  saveDisabled,
}: {
  error: string | null;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  saveTestId: string;
  saveLabel?: string;
  saveDisabled?: boolean;
}) {
  return (
    <>
      {error && <Alert color="red">{error}</Alert>}
      <Group justify="flex-end">
        <Button variant="default" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button data-testid={saveTestId} loading={saving} disabled={saveDisabled} onClick={onSave}>
          {saveLabel}
        </Button>
      </Group>
    </>
  );
}

export function PropertyEditDialog({
  entity,
  onClose,
  onSaved,
}: DialogProps<{
  id: string;
  street: string;
  zip: string;
  city: string;
  externalNo?: string | null;
  accessNote?: string | null;
  distanceKm?: number | null;
  archived: boolean;
}>) {
  // Vollanalyse M13: gemeinsames Dialog-Gerüst (form/val/error + Mutation-Verdrahtung).
  const { form, setForm, error, setError, val, mutation } = useEditDialogForm<
    string | number | boolean
  >({ onClose, onSaved });
  const update = useCustomersControllerUpdateProperty<ApiError>({ mutation });
  // N4: km-Vorschlag (Werkstatt → gespeicherte Objektadresse) als überschreibbarer
  // Wert ins Feld übernehmen; ein Fehler blockiert den Editor nicht (nur Hinweis).
  const routeDistance = useRoutingControllerDistance<ApiError>({
    mutation: {
      onSuccess: (res) => setForm((f) => ({ ...f, distanceKm: res.distanceKm })),
      onError: (e) => setError(e.message),
    },
  });
  if (!entity) return null;
  return (
    <Modal opened onClose={onClose} title="Gebäude bearbeiten">
      <Stack gap="md">
        <TextInput
          label="Straße"
          value={val('street', entity.street)}
          onChange={(e) => setForm({ ...form, street: e.currentTarget.value })}
        />
        <Group grow>
          <TextInput
            label="PLZ"
            value={val('zip', entity.zip)}
            onChange={(e) => setForm({ ...form, zip: e.currentTarget.value })}
          />
          <TextInput
            label="Ort"
            value={val('city', entity.city)}
            onChange={(e) => setForm({ ...form, city: e.currentTarget.value })}
          />
        </Group>
        <Group grow>
          <TextInput
            label="Objektnummer (WoWi)"
            value={val('externalNo', entity.externalNo ?? '')}
            onChange={(e) => setForm({ ...form, externalNo: e.currentTarget.value })}
          />
          <NumberInput
            label="Entfernung (km)"
            value={
              form.distanceKm !== undefined ? Number(form.distanceKm) : (entity.distanceKm ?? '')
            }
            onChange={(v) => setForm({ ...form, distanceKm: v === '' ? '' : Number(v) })}
            decimalScale={1}
            min={0}
            data-testid="property-distance"
          />
        </Group>
        <Button
          variant="light"
          size="xs"
          loading={routeDistance.isPending}
          onClick={() => routeDistance.mutate({ id: entity.id })}
          data-testid="property-distance-compute"
        >
          Entfernung berechnen
        </Button>
        <TextInput
          label="Zugangshinweis"
          value={val('accessNote', entity.accessNote ?? '')}
          onChange={(e) => setForm({ ...form, accessNote: e.currentTarget.value })}
        />
        <Checkbox
          label="Archiviert"
          checked={Boolean(form.archived ?? entity.archived)}
          onChange={(e) => setForm({ ...form, archived: e.currentTarget.checked })}
          data-testid="property-archived"
        />
        <EditDialogFooter
          error={error}
          onCancel={onClose}
          saving={update.isPending}
          saveTestId="property-save"
          onSave={() =>
            update.mutate({
              id: entity.id,
              data: {
                street: val('street', entity.street),
                zip: val('zip', entity.zip),
                city: val('city', entity.city),
                externalNo: val('externalNo', entity.externalNo ?? '') || null,
                accessNote: val('accessNote', entity.accessNote ?? '') || null,
                distanceKm:
                  form.distanceKm === ''
                    ? null
                    : Number(form.distanceKm ?? entity.distanceKm ?? 0) || null,
                archived: Boolean(form.archived ?? entity.archived),
              },
            })
          }
        />
      </Stack>
    </Modal>
  );
}

export function UnitEditDialog({
  entity,
  onClose,
  onSaved,
}: DialogProps<{
  id: string;
  label: string;
  leaseNumber?: string | null;
  floor?: string | null;
  position?: string | null;
  tenantName?: string | null;
  tenantPhone?: string | null;
  tenantEmail?: string | null;
  archived: boolean;
}>) {
  // Vollanalyse M13: gemeinsames Dialog-Gerüst (form/val/error + Mutation-Verdrahtung).
  const { form, setForm, error, val, mutation } = useEditDialogForm<string | boolean>({
    onClose,
    onSaved,
  });
  const update = useCustomersControllerUpdateUnit<ApiError>({ mutation });
  if (!entity) return null;
  return (
    <Modal opened onClose={onClose} title="Wohnung bearbeiten">
      <Stack gap="md">
        <TextInput
          label="Bezeichnung"
          value={val('label', entity.label)}
          onChange={(e) => setForm({ ...form, label: e.currentTarget.value })}
        />
        {/* P5-A §2: Mietvertragsnummer als String (Format „72107.0009.03“ mit
            drittem Segment), Stockwerk und Lage L/R aus dem Belegkopf. */}
        <Group grow>
          <TextInput
            label="Mietvertragsnr."
            placeholder="z. B. 72107.0009.03"
            value={val('leaseNumber', entity.leaseNumber ?? '')}
            onChange={(e) => setForm({ ...form, leaseNumber: e.currentTarget.value })}
            data-testid="unit-lease-number"
          />
          <TextInput
            label="Stockwerk"
            placeholder="z. B. 1. OG"
            value={val('floor', entity.floor ?? '')}
            onChange={(e) => setForm({ ...form, floor: e.currentTarget.value })}
            data-testid="unit-floor"
          />
          <TextInput
            label="Lage"
            placeholder="L / R"
            value={val('position', entity.position ?? '')}
            onChange={(e) => setForm({ ...form, position: e.currentTarget.value })}
            data-testid="unit-position"
          />
        </Group>
        <TextInput
          label="Mieter"
          value={val('tenantName', entity.tenantName ?? '')}
          onChange={(e) => setForm({ ...form, tenantName: e.currentTarget.value })}
        />
        <Group grow>
          <TextInput
            label="Telefon"
            value={val('tenantPhone', entity.tenantPhone ?? '')}
            onChange={(e) => setForm({ ...form, tenantPhone: e.currentTarget.value })}
          />
          <TextInput
            label="E-Mail"
            value={val('tenantEmail', entity.tenantEmail ?? '')}
            onChange={(e) => setForm({ ...form, tenantEmail: e.currentTarget.value })}
            data-testid="unit-tenant-email"
          />
        </Group>
        <Checkbox
          label="Archiviert"
          checked={Boolean(form.archived ?? entity.archived)}
          onChange={(e) => setForm({ ...form, archived: e.currentTarget.checked })}
        />
        <EditDialogFooter
          error={error}
          onCancel={onClose}
          saving={update.isPending}
          saveTestId="unit-save"
          onSave={() =>
            update.mutate({
              id: entity.id,
              data: {
                label: val('label', entity.label),
                leaseNumber: val('leaseNumber', entity.leaseNumber ?? '') || null,
                floor: val('floor', entity.floor ?? '') || null,
                position: val('position', entity.position ?? '') || null,
                tenantName: val('tenantName', entity.tenantName ?? '') || null,
                tenantPhone: val('tenantPhone', entity.tenantPhone ?? '') || null,
                tenantEmail: val('tenantEmail', entity.tenantEmail ?? '') || null,
                archived: Boolean(form.archived ?? entity.archived),
              },
            })
          }
        />
      </Stack>
    </Modal>
  );
}

export function ContactEditDialog({
  entity,
  createCustomerId,
  createPropertyId,
  onClose,
  onSaved,
}: DialogProps<{
  id: string;
  role: string;
  name: string;
  phone?: string | null;
  email?: string | null;
}> & {
  /** Anlege-Modus: kundenweiten Ansprechpartner NEU erfassen (P4-M5b). */
  createCustomerId?: string | null;
  /** Anlege-Modus: Gebäude-Kontakt (Hausmeister, 4.7) NEU erfassen — der
      Kontakt hängt dann am Gebäude, Rolle vorbelegt CARETAKER (änderbar). */
  createPropertyId?: string | null;
}) {
  // Vollanalyse M13: gemeinsames Dialog-Gerüst; wegen des Anlege-Modus werden
  // die Deltas nach dem Speichern verworfen (Dialog wird wiederverwendet).
  const { form, setForm, error, val, mutation } = useEditDialogForm<string>({
    onClose,
    onSaved,
    resetOnSuccess: true,
  });
  const update = useCustomersControllerUpdateContact<ApiError>({ mutation });
  const create = useCustomersControllerCreateContact<ApiError>({ mutation });
  const isCreate = !entity && !!(createCustomerId || createPropertyId);
  if (!entity && !isCreate) return null;
  const name = val('name', entity?.name ?? '');
  const role = val('role', entity?.role ?? (createPropertyId ? 'CARETAKER' : 'BILLING'));
  const phone = val('phone', entity?.phone ?? '') || null;
  const email = val('email', entity?.email ?? '') || null;
  const pending = update.isPending || create.isPending;
  return (
    <Modal
      opened
      onClose={onClose}
      title={
        isCreate
          ? createPropertyId
            ? 'Hausmeister hinzufügen'
            : 'Ansprechpartner hinzufügen'
          : 'Ansprechpartner bearbeiten'
      }
    >
      <Stack gap="md">
        <TextInput
          label="Name"
          data-testid="contact-name"
          value={name}
          onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
        />
        <Select
          label="Rolle"
          data-testid="contact-dialog-role"
          data={[
            { value: 'CARETAKER', label: 'Hausmeister' },
            { value: 'PROPERTY_MGMT', label: 'Hausverwaltung' },
            { value: 'TENANT', label: 'Mieter' },
            { value: 'BILLING', label: 'Rechnungswesen' },
            { value: 'OTHER', label: 'Sonstige' },
          ]}
          value={role}
          onChange={(v) => v && setForm({ ...form, role: v })}
        />
        <Group grow>
          <TextInput
            label="Telefon"
            value={val('phone', entity?.phone ?? '')}
            onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
          />
          <TextInput
            label="E-Mail"
            value={val('email', entity?.email ?? '')}
            onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
          />
        </Group>
        <EditDialogFooter
          error={error}
          onCancel={onClose}
          saving={pending}
          saveTestId="contact-save"
          saveDisabled={!name.trim()}
          saveLabel={isCreate ? 'Hinzufügen' : 'Speichern'}
          onSave={() =>
            isCreate
              ? create.mutate({
                  // Gebäude-Kontakt (4.7) hängt am Gebäude, sonst kundenweit.
                  data: createPropertyId
                    ? { propertyId: createPropertyId, name, role: role as never, phone, email }
                    : { customerId: createCustomerId, name, role: role as never, phone, email },
                })
              : update.mutate({
                  id: entity!.id,
                  data: { name, role: role as never, phone, email },
                })
          }
        />
      </Stack>
    </Modal>
  );
}

export function CustomerEditDialog({
  entity,
  onClose,
  onSaved,
}: DialogProps<{
  id: string;
  kind: 'COMPANY' | 'PRIVATE';
  salutation?: string | null;
  displayName: string;
  legalName?: string | null;
  email?: string | null;
  phone?: string | null;
  vatId?: string | null;
  invoiceChannel: string;
  paymentTermDays: number | null;
  skontoPct?: number | null;
  skontoDays?: number | null;
  skontoActive: boolean;
  allowCollectiveInvoice: boolean;
  workReportWithoutTravel?: boolean;
  datevDebtorNo?: number | null;
  serviceRecipientNote?: string | null;
  archived: boolean;
  travelBilling?: ({ mode: string } & Record<string, unknown>) | null;
  distanceKm?: number | null;
  billingAddress?: {
    street?: string | null;
    zip?: string | null;
    city?: string | null;
    countryCode?: string | null;
    addressee?: string | null;
  } | null;
}>) {
  // Vollanalyse M13: gemeinsames Dialog-Gerüst (v-Helfer + Mutation-Verdrahtung).
  const { form, setForm, error, setError, v, mutation } = useEditDialogForm({ onClose, onSaved });
  // Vollanalyse M19: Liste serverseitig gekappt (Standard 200) — die Fahrt-
  // Artikel-Selects haben keine Such-Verdrahtung und fordern bewusst das
  // Maximum an (kleiner Artikel-Stamm; Datanorm liegt in Kundenpreislisten).
  const articles = useArticlesControllerList({ archived: 'false', take: 1000 });
  const setTravel = useBillingControllerSetTravelBilling<ApiError>();
  const update = useCustomersControllerUpdate<ApiError>({ mutation });
  // Kundentest 2026-08: km-Vorschlag Werkstatt → Rechnungsadresse — Entfernung
  // für ALLE Kunden (Fahrt-Fallback für Aufträge ohne Gebäude).
  const routeDistance = useRoutingControllerCustomerDistance<ApiError>({
    mutation: {
      onSuccess: (res) => setForm((f) => ({ ...f, distanceKm: res.distanceKm })),
      onError: (e) => setError(e.message),
    },
  });
  if (!entity) return null;
  const tb = entity.travelBilling ?? null;
  const travelMode = v('travelMode', tb?.mode ?? 'NONE') as string;
  // P5-A §1: Privat/Geschäftlich steuert nur die Sichtbarkeit der
  // geschäftsspezifischen Felder (UstID, Debitor). Reine Präsentation — beim
  // Umschalten auf Privat bleiben die Werte erhalten, sie werden nur ausgeblendet.
  const kind = v('kind', entity.kind) as 'COMPANY' | 'PRIVATE';
  const isCompany = kind === 'COMPANY';
  const articleOptions = (articles.data ?? []).map((a) => ({
    value: a.id,
    label: `${a.sku} · ${a.name}`,
  }));

  /** Aus den Formularfeldern das travelBilling-Objekt (4.8) bauen. */
  const buildTravelBilling = (): Record<string, unknown> | null => {
    switch (travelMode) {
      case 'NONE':
        return { mode: 'NONE' };
      case 'FLAT': {
        const articleId = v('travelArticleId', (tb?.articleId as string) ?? '');
        return articleId ? { mode: 'FLAT', articleId } : null;
      }
      case 'PER_KM': {
        const rateEuro = Number(v('travelRateEuro', ((tb?.ratePerKmCents as number) ?? 0) / 100));
        const kmFactor = Number(v('travelKmFactor', (tb?.kmFactor as number) ?? 2));
        return { mode: 'PER_KM', ratePerKmCents: Math.round(rateEuro * 100), kmFactor };
      }
      case 'TRIP_POSITIONS': {
        const firstArticleId = v('travelFirstArticleId', (tb?.firstArticleId as string) ?? '');
        const nextArticleId = v('travelNextArticleId', (tb?.nextArticleId as string) ?? '');
        return firstArticleId && nextArticleId
          ? { mode: 'TRIP_POSITIONS', firstArticleId, nextArticleId }
          : null;
      }
      case 'ZONES':
        return { mode: 'ZONES' };
      default:
        return null;
    }
  };
  return (
    <Modal opened onClose={onClose} title="Kunde bearbeiten" size="lg">
      <Stack gap="md">
        <Group grow>
          <Select
            label="Art"
            data={[
              { value: 'COMPANY', label: 'Firma' },
              { value: 'PRIVATE', label: 'Privat' },
            ]}
            allowDeselect={false}
            value={kind}
            onChange={(val) => val && setForm({ ...form, kind: val })}
            data-testid="customer-edit-kind"
          />
          <TextInput
            label="Anrede"
            placeholder="z. B. Herr, Frau"
            value={v('salutation', entity.salutation ?? '')}
            onChange={(e) => setForm({ ...form, salutation: e.currentTarget.value })}
            data-testid="customer-edit-salutation"
          />
        </Group>
        <Group grow>
          <TextInput
            label="Anzeigename"
            value={v('displayName', entity.displayName)}
            onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
            data-testid="customer-edit-name"
          />
          <TextInput
            label="Firmierung (legal)"
            value={v('legalName', entity.legalName ?? '')}
            onChange={(e) => setForm({ ...form, legalName: e.currentTarget.value })}
          />
        </Group>
        {/* Faruq 2026-09-03: Die Rechnungsanschrift war hier nicht änderbar —
            sie stand nur in der Kundenakte. Pflicht sind Straße, PLZ und Ort. */}
        <Group grow align="flex-end">
          <TextInput
            label="Straße"
            value={v('street', entity.billingAddress?.street ?? '')}
            onChange={(e) => setForm({ ...form, street: e.currentTarget.value })}
            data-testid="customer-edit-street"
          />
          <TextInput
            label="PLZ"
            value={v('zip', entity.billingAddress?.zip ?? '')}
            onChange={(e) => setForm({ ...form, zip: e.currentTarget.value })}
            data-testid="customer-edit-zip"
          />
          <TextInput
            label="Ort"
            value={v('city', entity.billingAddress?.city ?? '')}
            onChange={(e) => setForm({ ...form, city: e.currentTarget.value })}
            data-testid="customer-edit-city"
          />
        </Group>
        <TextInput
          label="Adresszusatz / Empfänger (optional)"
          placeholder="z. B. Hausverwaltung, z. Hd. …"
          value={v('addressee', entity.billingAddress?.addressee ?? '')}
          onChange={(e) => setForm({ ...form, addressee: e.currentTarget.value })}
          data-testid="customer-edit-addressee"
        />
        <Group grow>
          <TextInput
            label="E-Mail (Rechnungseingang)"
            value={v('email', entity.email ?? '')}
            onChange={(e) => setForm({ ...form, email: e.currentTarget.value })}
          />
          <TextInput
            label="Telefon"
            value={v('phone', entity.phone ?? '')}
            onChange={(e) => setForm({ ...form, phone: e.currentTarget.value })}
          />
          {isCompany && (
            <TextInput
              label="USt-IdNr."
              value={v('vatId', entity.vatId ?? '')}
              onChange={(e) => setForm({ ...form, vatId: e.currentTarget.value })}
              data-testid="customer-edit-vatid"
            />
          )}
        </Group>
        <Group grow>
          <Select
            label="Rechnungskanal"
            data={selectOptions(CHANNEL_LABELS)}
            value={v('invoiceChannel', entity.invoiceChannel)}
            onChange={(val) => val && setForm({ ...form, invoiceChannel: val })}
            data-testid="customer-edit-channel"
          />
          {isCompany && (
            <NumberInput
              label="Debitorennummer"
              value={v('datevDebtorNo', entity.datevDebtorNo ?? '') as number | ''}
              onChange={(val) =>
                setForm({ ...form, datevDebtorNo: val === '' ? null : Number(val) })
              }
              data-testid="customer-edit-debtor"
            />
          )}
        </Group>
        <Group grow align="flex-end">
          <NumberInput
            label="Zahlungsziel (Tage)"
            description="Leer = keine „Zahlbar bis“-Zeile auf der Rechnung"
            value={v('paymentTermDays', entity.paymentTermDays ?? '') as number | ''}
            onChange={(val) =>
              setForm({ ...form, paymentTermDays: val === '' ? null : Number(val) })
            }
            min={0}
            data-testid="customer-edit-payment-term"
          />
          <NumberInput
            label="Skonto %"
            value={v('skontoPct', entity.skontoPct ?? '') as number | ''}
            onChange={(val) => setForm({ ...form, skontoPct: val === '' ? null : Number(val) })}
            decimalScale={2}
            min={0}
          />
          <NumberInput
            label="Skonto Tage"
            value={v('skontoDays', entity.skontoDays ?? '') as number | ''}
            onChange={(val) => setForm({ ...form, skontoDays: val === '' ? null : Number(val) })}
            min={0}
          />
          <Checkbox
            label="Skonto aktiv"
            checked={v('skontoActive', entity.skontoActive)}
            onChange={(e) => setForm({ ...form, skontoActive: e.currentTarget.checked })}
            data-testid="customer-edit-skonto-active"
          />
        </Group>
        <Checkbox
          label="Archiviert"
          checked={v('archived', entity.archived)}
          onChange={(e) => setForm({ ...form, archived: e.currentTarget.checked })}
        />

        <Checkbox
          label="Sammelrechnung erlauben"
          description="Erst nach dokumentierter schriftlicher Bestätigung durch die Wohnungsgesellschaft freischalten."
          checked={v('allowCollectiveInvoice', entity.allowCollectiveInvoice)}
          onChange={(e) => setForm({ ...form, allowCollectiveInvoice: e.currentTarget.checked })}
          data-testid="customer-edit-collective"
        />
        {/* Tobias 2026-09-25: z. B. Bischof 10314/10346 — dort keine Fahrtkosten
            auf dem Arbeitsnachweis; Kleinmaterial bleibt. */}
        <Checkbox
          label="Arbeitsnachweis ohne Fahrtkosten-Pauschale"
          description="Die Position „Fahrtkosten“ wird beim Arbeitsnachweis dieses Kunden nicht automatisch eingefügt."
          checked={v('workReportWithoutTravel', entity.workReportWithoutTravel ?? false)}
          onChange={(e) => setForm({ ...form, workReportWithoutTravel: e.currentTarget.checked })}
          data-testid="customer-edit-ln-ohne-fahrt"
        />

        {/* INTREAL-Konstruktion (Sage-Rechnung 20230821): fester mehrzeiliger
            „Leistungsempfänger:"-Block auf jedem Beleg dieses Kunden. Die
            Postanschrift im Fenster bleibt die Rechnungsadresse. */}
        <Textarea
          label="Leistungsempfänger auf Belegen (optional)"
          description={
            'Erscheint als eigener Block „Leistungsempfänger:" auf jedem Beleg — z. B. KVG „handelnd für das Sondervermögen …". Eine Zeile je Belegzeile.'
          }
          minRows={3}
          value={v('serviceRecipientNote', entity.serviceRecipientNote ?? '') as string}
          onChange={(e) => setForm({ ...form, serviceRecipientNote: e.currentTarget.value })}
          data-testid="customer-edit-service-recipient"
        />

        <Divider label="Fahrtberechnung (Phase 2, 4.8)" labelPosition="left" />
        {/* Kundentest 2026-08: Entfernung für ALLE Kunden — so ist beim Wählen
            des Abrechnungsmodus schon klar, welche Strecke anfällt. Am Auftrag
            gilt weiter: Gebäude-Entfernung vor Kunden-Entfernung. */}
        <Group grow align="flex-end">
          <NumberInput
            label="Entfernung Werkstatt → Kunde (km)"
            description="Fallback für Aufträge ohne Gebäude; Gebäude-Entfernung geht vor."
            value={
              form.distanceKm !== undefined
                ? (form.distanceKm as number | '')
                : (entity.distanceKm ?? '')
            }
            onChange={(val) => setForm({ ...form, distanceKm: val === '' ? '' : Number(val) })}
            decimalScale={1}
            min={0}
            data-testid="customer-distance"
          />
          <Button
            variant="light"
            loading={routeDistance.isPending}
            onClick={() => routeDistance.mutate({ id: entity.id })}
            data-testid="customer-distance-compute"
          >
            Entfernung berechnen
          </Button>
        </Group>
        <Group grow align="flex-start">
          <Select
            label="Modus"
            data={TRAVEL_MODES}
            value={travelMode}
            onChange={(val) => val && setForm({ ...form, travelMode: val })}
            data-testid="customer-travel-mode"
          />
          {travelMode === 'PER_KM' && (
            <>
              <NumberInput
                label="Preis je km (€)"
                decimalScale={2}
                min={0}
                value={v('travelRateEuro', ((tb?.ratePerKmCents as number) ?? 0) / 100) as number}
                onChange={(val) => setForm({ ...form, travelRateEuro: Number(val) || 0 })}
              />
              <NumberInput
                label="km-Faktor (Hin/Rück = 2)"
                decimalScale={1}
                min={0}
                value={v('travelKmFactor', (tb?.kmFactor as number) ?? 2) as number}
                onChange={(val) => setForm({ ...form, travelKmFactor: Number(val) || 0 })}
              />
            </>
          )}
          {travelMode === 'FLAT' && (
            <Select
              label="Fahrt-Artikel"
              searchable
              data={articleOptions}
              value={v('travelArticleId', (tb?.articleId as string) ?? '') as string}
              onChange={(val) => setForm({ ...form, travelArticleId: val ?? '' })}
            />
          )}
        </Group>
        {travelMode === 'TRIP_POSITIONS' && (
          <Group grow>
            <Select
              label="Erste Fahrt (Artikel)"
              searchable
              data={articleOptions}
              value={v('travelFirstArticleId', (tb?.firstArticleId as string) ?? '') as string}
              onChange={(val) => setForm({ ...form, travelFirstArticleId: val ?? '' })}
            />
            <Select
              label="Weitere Fahrten (Artikel)"
              searchable
              data={articleOptions}
              value={v('travelNextArticleId', (tb?.nextArticleId as string) ?? '') as string}
              onChange={(val) => setForm({ ...form, travelNextArticleId: val ?? '' })}
            />
          </Group>
        )}
        {travelMode === 'ZONES' && (
          <Text size="sm" c="dimmed">
            Zonen (maxKm + Artikel) werden in den Einstellungen gepflegt; als Entfernung zählt das
            Gebäude des Auftrags, sonst die Kunden-Entfernung oben.
          </Text>
        )}
        {travelMode === 'PER_KM' && (
          <Text size="sm" c="dimmed">
            Menge je Fahrt = Entfernung × Faktor (Gebäude des Auftrags, sonst Kunden-Entfernung).
          </Text>
        )}

        {/* Archivieren: ausgeschiedene/inaktive Kunden ausblenden. Sie
            erscheinen dann nur mit „Archivierte anzeigen" in der Liste und
            bleiben (GoBD) vollständig erhalten. */}
        <Checkbox
          label="Kunde archiviert"
          description="Blendet den Kunden aus der Standardliste aus (reaktivierbar)."
          checked={v('archived', entity.archived)}
          onChange={(e) => setForm({ ...form, archived: e.currentTarget.checked })}
          data-testid="customer-edit-archived"
        />

        <EditDialogFooter
          error={error}
          onCancel={onClose}
          saving={update.isPending || setTravel.isPending}
          saveTestId="customer-edit-save"
          onSave={async () => {
            setError(null);
            const street = (v('street', entity.billingAddress?.street ?? '') as string).trim();
            const zip = (v('zip', entity.billingAddress?.zip ?? '') as string).trim();
            const city = (v('city', entity.billingAddress?.city ?? '') as string).trim();
            if (!street || !zip || !city) {
              setError('Bitte Straße, PLZ und Ort der Rechnungsanschrift ausfüllen.');
              return;
            }
            const addressee = (
              v('addressee', entity.billingAddress?.addressee ?? '') as string
            ).trim();
            try {
              const travelBilling = buildTravelBilling();
              if (travelBilling) {
                await setTravel.mutateAsync({
                  id: entity.id,
                  data: { travelBilling: travelBilling as never },
                });
              }
              update.mutate({
                id: entity.id,
                data: {
                  kind,
                  billingAddress: {
                    street,
                    zip,
                    city,
                    countryCode: entity.billingAddress?.countryCode || 'DE',
                    ...(addressee ? { addressee } : {}),
                  },
                  salutation: (v('salutation', entity.salutation ?? '') as string) || null,
                  displayName: v('displayName', entity.displayName),
                  legalName: (v('legalName', entity.legalName ?? '') as string) || null,
                  email: (v('email', entity.email ?? '') as string) || null,
                  phone: (v('phone', entity.phone ?? '') as string) || null,
                  vatId: (v('vatId', entity.vatId ?? '') as string) || null,
                  invoiceChannel: v('invoiceChannel', entity.invoiceChannel) as never,
                  paymentTermDays: v('paymentTermDays', entity.paymentTermDays ?? null) as
                    number | null,
                  skontoPct: v('skontoPct', entity.skontoPct ?? null) as number | null,
                  skontoDays: v('skontoDays', entity.skontoDays ?? null) as number | null,
                  skontoActive: v('skontoActive', entity.skontoActive),
                  allowCollectiveInvoice: v(
                    'allowCollectiveInvoice',
                    entity.allowCollectiveInvoice,
                  ),
                  workReportWithoutTravel: v(
                    'workReportWithoutTravel',
                    entity.workReportWithoutTravel ?? false,
                  ),
                  datevDebtorNo: v('datevDebtorNo', entity.datevDebtorNo ?? null) as number | null,
                  serviceRecipientNote:
                    (
                      v('serviceRecipientNote', entity.serviceRecipientNote ?? '') as string
                    ).trim() || null,
                  distanceKm:
                    form.distanceKm === ''
                      ? null
                      : Number(form.distanceKm ?? entity.distanceKm ?? 0) || null,
                  archived: v('archived', entity.archived),
                },
              });
            } catch (e) {
              setError((e as Error).message);
            }
          }}
        />
      </Stack>
    </Modal>
  );
}

/**
 * Löschen mit Rückfrage und Referenzprüfung (4.1b).
 *
 * Faruq 2026-08-24: „Jedes Löschen im Web sollte mit einem Bestätigungsmodal
 * belegt werden, da löschen = komplett gelöscht bedeutet." Vorher loeschte
 * dieser Knopf beim ERSTEN Klick, ohne jede Rueckfrage — in einer engen
 * Tabellenzeile neben „Bearbeiten" ist das ein Griff daneben und der Datensatz
 * ist weg. Jetzt oeffnet er ein Modal; geloescht wird erst dort.
 *
 * Bewusst ein Modal und kein Inline-Dialog: der Inline-Dialog stand mitten in
 * der Zeile, sein Bestaetigen-Knopf landete genau dort, wo eben noch „Loeschen"
 * war — ein zweiter Klick aus Gewohnheit reichte. Das Modal erscheint woanders
 * und faengt genau das ab.
 *
 * Die Serverantwort (z. B. „Artikel haengt noch an 3 Belegen") steht IM Modal:
 * dort, wo man gehandelt hat.
 */
export function DeleteButton({
  label,
  onDelete,
  testId,
  variant = 'subtle',
  size = 'compact-xs',
  title,
  question,
  detail,
  confirmLabel = 'Löschen',
  asIcon = false,
  iconName = 'remove',
}: {
  label: string;
  onDelete: () => Promise<unknown>;
  testId?: string;
  /** Darstellung: Standard ist der dezente Inline-Stil der Stammdaten-Listen;
      'outline' = Rahmen-Button in Rot fuer freistehende Aktionen; prominente
      Stellen (Auftragskopf) nutzen 'filled' = rot mit weissem Text. */
  variant?: 'subtle' | 'outline' | 'filled';
  size?: string;
  /** Titel des Modals; Vorgabe ist die Beschriftung des Knopfes. */
  title?: string;
  /** Die eigentliche Frage; Vorgabe passt fuer jeden Datensatz. */
  question?: string;
  /** Ein Satz darunter in Grau — was genau verschwindet. */
  detail?: string;
  confirmLabel?: string;
  /**
   * Zeilenaktion in einer Tabelle: rotes Kreuz statt Beschriftung (Faruq
   * 2026-08-24, „wie aus einem Guss"). `label` bleibt Pflicht — es wird zum
   * zugaenglichen Namen und zum Titel, denn ohne Beschriftung ist der Name
   * die einzige Erklaerung.
   */
  asIcon?: boolean;
  /** Symbol bei `asIcon` — Vorgabe Kreuz; Faruq 2026-10-30: Bestellungen nehmen den Mülleimer. */
  iconName?: string;
}) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offen, setOffen] = useState(false);

  const schliessen = () => {
    if (busy) return;
    setOffen(false);
    setError(null);
  };

  return (
    <>
      {asIcon ? (
        <ActionIcon
          variant="subtle"
          color="red"
          size="md"
          aria-label={title ?? label}
          title={title ?? label}
          data-testid={testId}
          onClick={() => {
            setError(null);
            setOffen(true);
          }}
        >
          <Icon name={iconName} size={17} />
        </ActionIcon>
      ) : (
        <Button
          variant={variant}
          color="red"
          size={size}
          data-testid={testId}
          onClick={() => {
            setError(null);
            setOffen(true);
          }}
        >
          {label}
        </Button>
      )}
      <Modal opened={offen} onClose={schliessen} title={title ?? label} centered>
        <Stack gap="md">
          <Text size="sm">{question ?? 'Soll dieser Eintrag wirklich gelöscht werden?'}</Text>
          <Text size="sm" c="dimmed">
            {detail ?? 'Gelöscht ist gelöscht — rückgängig machen lässt sich das nicht.'}
          </Text>
          {error && (
            <Alert color="orange" variant="light" data-testid="delete-blocked">
              {error}
            </Alert>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={schliessen} disabled={busy}>
              Abbrechen
            </Button>
            <Button
              color="red"
              loading={busy}
              data-testid={testId ? `${testId}-confirm` : undefined}
              onClick={async () => {
                setBusy(true);
                try {
                  await onDelete();
                  setError(null);
                  setOffen(false);
                } catch (e) {
                  // Vollanalyse S6: Kopf-Meldung + Details über den gemeinsamen
                  // Feld-Fehler-Vertrag (lib/fieldErrors) statt ad-hoc-Body-Parsing.
                  // Das Modal bleibt offen — der Grund gehört an die Stelle, an
                  // der man gehandelt hat.
                  setError(formatFieldErrors(e, 'Löschen nicht möglich.'));
                } finally {
                  setBusy(false);
                }
              }}
            >
              {confirmLabel}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
